import { CheckIcon, ChevronDown, GlobeLock, SettingsIcon } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useEffect, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/molecules/tooltip";
import { useSettings } from "@/hooks/use-settings";
import { getEnabledEngines, type SearchEngine } from "@/lib/search-engine-config";
import { addEntry } from "@/lib/search-history";
import { cn } from "@/lib/utils";

function LazyIcon({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <span className="size-4 rounded inline-flex items-center justify-center bg-muted shrink-0">
      <img
        src={src}
        alt={alt}
        className={cn(
          "size-4 rounded transition-opacity duration-150",
          loaded ? "opacity-100" : "opacity-0",
        )}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(false)}
      />
    </span>
  );
}

function getSavedEngineId(): string {
  try {
    return localStorage.getItem("kourai-search-engine") ?? "google";
  } catch {
    return "google";
  }
}

function getEngines(): SearchEngine[] {
  return getEnabledEngines();
}

export function getEngine(id: string): SearchEngine | undefined {
  const engines = getEngines();
  return engines.find((e) => e.id === id);
}

export function dispatchSearch(query: string, workspaceId?: string | null) {
  const engine = getCurrentEngine();
  if (!engine) return;

  if (query.trim()) {
    const searchUrl = engine.url.replace("{query}", encodeURIComponent(query.trim()));
    addEntry({ query: query.trim(), type: "web", engineOrModel: engine.id }, workspaceId);
    window.location.href = searchUrl;
  }
}

/** The engine web searches go to: the saved choice, else the first enabled one. */
export function getCurrentEngine(): SearchEngine | undefined {
  const engines = getEngines();
  return engines.find((e) => e.id === getSavedEngineId()) ?? engines[0];
}

export function SearchEngineSelector() {
  const [engines, setEngines] = useState<SearchEngine[]>(getEngines);
  const [selected, setSelected] = useState(getSavedEngineId);
  const { openSettings } = useSettings();

  useEffect(() => {
    localStorage.setItem("kourai-search-engine", selected);
  }, [selected]);

  const current = getEngine(selected);

  if (!current || engines.length === 0) return null;

  return (
    <DropdownMenu.Root onOpenChange={(open) => open && setEngines(getEngines())}>
      <DropdownMenu.Trigger
        data-slot="search-engine-trigger"
        aria-label={`Search engine: ${current.label}`}
        className="focus-visible:ring-ring/50 text-muted-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent flex h-7 min-w-0 shrink items-center gap-1.5 rounded-full px-2.5 text-xs whitespace-nowrap transition-colors outline-none focus-visible:ring-2"
      >
        {current.icon && <LazyIcon src={current.icon} alt="" />}
        <span className="truncate">{current.label}</span>
        <ChevronDown className="size-3.5 shrink-0 opacity-50" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="bg-popover/95 text-popover-foreground data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 z-50 w-56 max-w-(--radix-dropdown-menu-content-available-width) overflow-hidden rounded-xl border p-1.5 shadow-lg backdrop-blur-sm"
        >
          <DropdownMenu.Label className="text-muted-foreground px-2.5 pt-1 pb-1.5 text-xs">
            Search with
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={selected} onValueChange={setSelected}>
            {engines.map((e) => (
              <DropdownMenu.RadioItem
                key={e.id}
                value={e.id}
                className="focus:bg-accent focus:text-accent-foreground relative flex min-w-0 cursor-pointer items-center gap-2 rounded-lg py-1.5 ps-2.5 pe-8 text-sm outline-none select-none"
              >
                {e.icon && <LazyIcon src={e.icon} alt="" />}
                <span className="truncate">{e.label}</span>
                {e.privacyUrl && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={e.privacyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${e.label} privacy policy`}
                        onClick={(ev) => ev.stopPropagation()}
                        onPointerDown={(ev) => ev.stopPropagation()}
                        className="text-success ml-auto flex shrink-0 items-center hover:opacity-80"
                      >
                        <GlobeLock className="size-3.5" />
                      </a>
                    </TooltipTrigger>
                    <TooltipContent side="top">Privacy-first search</TooltipContent>
                  </Tooltip>
                )}
                <DropdownMenu.ItemIndicator className="absolute end-2.5 top-1/2 -translate-y-1/2">
                  <CheckIcon className="size-4" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="bg-border -mx-1.5 my-1.5 h-px" />
          <DropdownMenu.Item
            onSelect={openSettings}
            className="text-muted-foreground focus:bg-accent focus:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none select-none"
          >
            <SettingsIcon className="size-3.5" />
            <span>Edit providers</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
